import React, { FC } from 'react';
import { Box, Flex } from '@edreamsodigeo/prisma-design-system';
import { Desktop, Mobile } from '@edreamsodigeo/frontend-core';

interface cssProperties {
  ribbon: {
    height: number;
    borderLeftRadiusForSummary: number;
    content: {
      paddingNoRibbonSide: string;
      paddingRibbonSide: string;
    };
  };
}

const desktopProperties: cssProperties = {
  ribbon: {
    height: 32,
    borderLeftRadiusForSummary: 0,
    content: {
      paddingNoRibbonSide: 'base',
      paddingRibbonSide: 'big',
    },
  },
};

const mobileProperties: cssProperties = {
  ribbon: {
    height: 26,
    borderLeftRadiusForSummary: 1,
    content: {
      paddingNoRibbonSide: '1x_small',
      paddingRibbonSide: 'base',
    },
  },
};

const backgroundRibbonRight =
  'linear-gradient(60deg, #209B87 13.06%, #75C5B8 80.17%)';
const backgroundRibbonLeft =
  'linear-gradient(-60deg, #209B87 13.06%, #75C5B8 80.17%)';

interface RibbonProps {
  ribbonPlacement: 'card' | 'summary';
  ribbonOrientation: 'right' | 'left';
  ribbonColor?: {
    background: string;
    fold: string;
  };
  ribbonWithGradient?: boolean;
}

interface RibbonLayoutProps {
  ribbonPlacement: 'card' | 'summary';
  ribbonOrientation: 'right' | 'left';
  ribbonProperties: cssProperties;
  ribbonColor?: {
    background: string;
    fold: string;
  };
  ribbonWithGradient?: boolean;
}

const Ribbon: FC<RibbonProps> = ({
  ribbonOrientation = 'right',
  ribbonPlacement = 'card',
  ribbonColor,
  ribbonWithGradient = true,
  children,
}) => (
  <>
    <Desktop>
      <RibbonLayout
        ribbonOrientation={ribbonOrientation}
        ribbonPlacement={ribbonPlacement}
        ribbonProperties={desktopProperties}
        ribbonColor={ribbonColor}
        ribbonWithGradient={ribbonWithGradient}
      >
        {children}
      </RibbonLayout>
    </Desktop>
    <Mobile>
      <RibbonLayout
        ribbonOrientation={ribbonOrientation}
        ribbonPlacement={ribbonPlacement}
        ribbonProperties={mobileProperties}
        ribbonColor={ribbonColor}
        ribbonWithGradient={ribbonWithGradient}
      >
        {children}
      </RibbonLayout>
    </Mobile>
  </>
);

const RibbonLayout: FC<RibbonLayoutProps> = ({
  ribbonOrientation = 'right',
  ribbonProperties,
  ribbonPlacement,
  ribbonColor,
  ribbonWithGradient,
  children,
}) => {
  const backgroundGradient =
    ribbonOrientation === 'right'
      ? backgroundRibbonRight
      : backgroundRibbonLeft;
  const marginSkew = ribbonOrientation === 'right' ? '0 0 0 -9px' : '0 0 0 9px';

  const skewGradient =
    ribbonOrientation === 'right' ? 'skew(-30deg)' : 'skew(30deg)';
  const skewGreen =
    ribbonOrientation === 'right' ? 'skew(30deg)' : 'skew(-30deg)';
  const paddingRight =
    ribbonOrientation === 'right'
      ? ribbonProperties.ribbon.content.paddingRibbonSide
      : ribbonProperties.ribbon.content.paddingNoRibbonSide;
  const paddingLeft =
    ribbonOrientation === 'right'
      ? ribbonProperties.ribbon.content.paddingNoRibbonSide
      : ribbonProperties.ribbon.content.paddingRibbonSide;

  const borderLeftRadius =
    ribbonOrientation === 'right' && ribbonPlacement === 'summary'
      ? ribbonProperties.ribbon.borderLeftRadiusForSummary
      : 1;

  return (
    <Flex
      sx={{
        height: ribbonProperties.ribbon.height,
        overflow: 'hidden',
        width: 'auto',
      }}
    >
      <Box
        sx={{ overflow: 'hidden' }}
        borderBottomLeftRadius={borderLeftRadius}
        borderTopLeftRadius={borderLeftRadius}
        borderBottomRightRadius={1}
        borderTopRightRadius={1}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            margin: marginSkew,
            transform: skewGreen,
            backgroundColor: ribbonColor ? ribbonColor.fold : 'green.50',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: '2',
          }}
        />
        <Box
          sx={{
            ...(ribbonWithGradient
              ? { background: backgroundGradient }
              : { backgroundColor: ribbonColor?.background }),
            top: -ribbonProperties.ribbon.height,
            width: '100%',
            height: '100%',
            margin: marginSkew,
            transform: skewGradient,
            overflow: 'hidden',
            position: 'relative',
            borderTopLeftRadius: '2',
            borderBottomLeftRadius: '1',
            borderTopRightRadius: '2',
            borderBottomRightRadius: '1',
          }}
        />
        <Flex
          alignItems="center"
          sx={{
            height: '100%',
            top: -ribbonProperties.ribbon.height * 2,
            position: 'relative',
            paddingRight,
            paddingLeft,
          }}
        >
          {children}
        </Flex>
      </Box>
    </Flex>
  );
};

export default Ribbon;
